<template>
  <el-table
    :data="tubeData"
    :header-cell-style="headerCellStyle"
    header-cell-class-name="tight-header"
    :style="{ margin: '15px 0 0 0', width: '100%' }"
    class="tube-table"
    :class="{ 'tube-table--compact': !expanded }"
    :show-header="expanded"
  >
    <template #empty>
      <span class="empty-text">{{ $t('OrderPage["No Data"]') }}</span>
    </template>
    <!-- 1st col: Tube images (only when expanded) -->
    <el-table-column v-if="expanded" :label="$t('OrderPage[\'Tube\']')" min-width="50">
      <template v-slot="scope">
        <div v-for="(tube, idx) in scope.row.tube" :key="idx">
          <img
            v-if="tube !== '.png' "
            :src="image_base_url + 'orderTest/tubes/' + tube"
            alt="tube icon"
          >
        </div>
      </template>
    </el-table-column>
    <!-- 2nd col -->
    <el-table-column :label="$t('OrderPage[\'Specimen Info\']')" min-width="80">
      <template v-slot="scope">
        <div
          v-for="(name, idx) in scope.row.specimenName"
          :key="idx"
          :class="[
            { 'mb-12': scope.row.specimenName.length > 1
                && idx < scope.row.specimenName.length - 1 }
          ]"
        >
          <p class="specimen-name">{{ name }}</p>
          <p class="specimen-info" v-for="(item, index) in scope.row.specimenInfo[idx].split(',')" :key="index">
            {{ item }}
          </p>
        </div>
      </template>
    </el-table-column>
    <!-- 3rd col -->
    <el-table-column :label="$t('OrderPage[\'Qty\']')" min-width="40">
      <template v-slot="scope">
        <p :class="['qty', { 'mb-24': scope.row.qty_top }]">
          {{ scope.row.qty }}
        </p>
      </template>
    </el-table-column>
    <!-- 4th col -->
    <el-table-column :label="$t('OrderPage[\'Volume Required\']')" min-width="100">
      <template v-slot="scope">
        <p :class="['volume', { 'mb-24': scope.row.qty_top }]">
          {{ scope.row.volume === '' ? `- -` : scope.row.volume }}
        </p>
      </template>
    </el-table-column>
  </el-table>
</template>

<script>
export default {
  inject: ['image_base_url'],
  props: {
    tubeData: {
      type: Array,
      default: () => []
    },
    expanded: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    headerCellStyle() {
      return {
        padding: "4px 2px",
        color: "var(--DividerGray, #C4C4C4)",
        fontFamily: "Inter",
        fontSize: "10px",
        fontWeight: "500",
        letterSpacing: "0.25px",
        lineHeight: "12px",
        textAlign: "center",
        background: "transparent",
        borderBottom: "none"
      }
    }
  }
}
</script>

<style scoped>
/* Override Element Plus table CSS variables */
.tube-table {
  --el-table-bg-color: transparent;
  --el-table-tr-bg-color: transparent;
  --el-table-header-bg-color: transparent;
  --el-table-row-hover-bg-color: transparent;
  --el-table-border-color: transparent;
  --el-table-border: none;
}

/* Transparent background to match parent #f9fbfd */
.tube-table,
.tube-table :deep(.el-table__header-wrapper),
.tube-table :deep(.el-table__body-wrapper),
.tube-table :deep(.el-table__inner-wrapper),
.tube-table :deep(table),
.tube-table :deep(tr),
.tube-table :deep(th.el-table__cell),
.tube-table :deep(td.el-table__cell),
.tube-table :deep(.el-table__body tr.hover-row > td.el-table__cell),
.tube-table :deep(.el-table__body tr > td.el-table__cell) {
  background: transparent !important;
  background-color: transparent !important;
}

/* Remove el-table bottom border line */
.tube-table :deep(.el-table__inner-wrapper::before) {
  display: none !important;
  height: 0 !important;
}

.tube-table :deep(.el-table__border-left-patch) {
  display: none;
}

/* Header row: dashed bottom border, relaxed spacing */
.tube-table :deep(.el-table__header .el-table__cell.tight-header .cell) {
  padding: 4px 2px;
  line-height: 12px;
  white-space: nowrap;
}

/* Header row bottom border — on the <tr> so it's a single dashed line */
.tube-table :deep(.el-table__header tr) {
  border-bottom: 1px dashed var(--DividerGray, #C4C4C4);
}

.tube-table :deep(.el-table__header .el-table__cell.tight-header) {
  border-bottom: none !important;
}

/* Dashed border between rows */
.tube-table :deep(td.el-table__cell) {
  border-bottom: 1px dashed var(--DividerGray, #C4C4C4);
}

/* Remove border on last row */
.tube-table :deep(.el-table__body tr:last-child td.el-table__cell) {
  border-bottom: none;
}

/* Compact mode: hide Tube column header if still rendered */
.tube-table--compact :deep(.el-table__header .el-table__cell.tight-header:first-child) {
  display: none;
}

/* Title style: specimen name */
.specimen-name {
  overflow: hidden;
  color: var(--TrueBlack, #333);
  text-overflow: ellipsis;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  margin-bottom: 2px;
}

/* Subtitle style: specimen info */
.specimen-info {
  overflow: hidden;
  color: var(--TrueBlack, #333);
  text-overflow: ellipsis;
  font-family: Inter;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.2px;
}

/* Qty style */
.qty {
  color: var(--TrueBlack, #333);
  text-align: right;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
}

/* Empty state */
.empty-text {
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #C4C4C4
}

/* Volume style */
.volume {
  color: var(--TrueBlack, #333);
  text-align: right;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
}
</style>
